import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { getMipOptionsForSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { CREATE_TRANSACTION_FORM_FIELD } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/useCreateNewTransactionDrawerController';

import {
	useTranslationCapManage,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	UpdateTransactionDto,
	UpdateTransactionShapeStrict,
	UserSubmittableTransactionType,
} from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	getInseparableRedemptionGroup,
	previewInseparableRedemption,
	updateInseparableRedemption,
	updateTransaction,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { getMipsForPersonByDate } from '@dealstack/frontend/app/services/InvestmentProgramme.service.ts';
import {
	CapTableRail,
	DataSecurityTct,
	TransactionThresholdExpressedAs,
	TransactionTitle,
	TransactionType,
	TransactionWithSecurityTct,
	UpdateInseparableRedemptionDto,
} from '@dealstack/shared/lib/generated';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useTransactionPivRelations } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionPivRelations';
import { useTransactionPartyDefaults } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionPartyDefaults';
import {
	getTransactionCostLabel,
	isNomineeInterestEditable,
	useTransactionFieldsVisibility,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import {
	getTransactionInterestText,
	getTransferInterestOptions,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import {
	InterestPairEditPlan,
	resolveInterestPairEditPlan,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/interestPairEdit.utils';
import { resolveTransactionFormSecurity } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/resolveTransactionFormSecurity.ts';
import { transactionMutationError } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/transactionMutationError.ts';
import {
	buildInseparableRedemptionDisclosure,
	InseparableRedemptionDisclosure,
	toLegFingerprint,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/CreateNewTransactionDrawer/inseparableRedemption.utils.ts';
import { useRedemptionCounterpartPreview } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useRedemptionCounterpartPreview.ts';

import {
	interestOptionsForPairedLeg,
	railFromTitle,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/EditTransactionDrawer/redemptionOperationRail.utils.ts';
import {
	formatDateToBEIgnoreTimezone,
	parseBEDateIgnoreTimezone,
} from '@dealstack/shared/lib/format/dates/date';

export enum EDIT_TRANSACTION_FORM_FIELD {
	DATE = 'date',
	SECURITY_ID = 'securityId',
	TYPE = 'type',
	BUYER = 'destinationId',
	SELLER = 'sourceId',
	BOUGHT_FOR = 'boughtForId',
	SOLD_FOR = 'soldForId',
	SELLER_MIP = 'fromMipId',
	BUYER_MIP = 'toMipId',
	NUMBER = 'units',
	PRICE = 'cost',
	CONVERSION_TARGET_SECURITY_ID = 'conversionTargetSecurityId',
	PARTICIPATION_THRESHOLD_EXPRESSED_AS = 'participationThresholdExpressedAs',
	PARTICIPATION_THRESHOLD = 'participationThreshold',
	TITLE = 'title',
}

export const useEditTransactionDrawerController = ({
	transaction,
	refreshTransactionsList,
	onClose,
	refetchTransactionDetails,
	securities,
	onTransactionReplaced,
}: {
	transaction?: TransactionWithSecurityTct;
	refreshTransactionsList?: () => void;
	onClose?: () => void;
	refetchTransactionDetails?: (transactionId: string) => void;
	securities?: DataSecurityTct[];
	// CAP-1976: an interest edit Both -> single on a REDEMPTION/CANCELLATION replaces the row with
	// the chosen-title twin; the details page uses this to land on the new id.
	onTransactionReplaced?: (newTransactionId: string) => void;
}) => {
	const tCapManage = useTranslationCapManage();
	const tDefault = useTranslationDefault();
	const [editMode, setEditMode] = useState(false);
	// CAP-1976: the pending pair dialog (blocked / confirm-and-sync / backdate warning / split
	// preview) computed from the dirty fields before an update is submitted.
	const [interestPairModal, setInterestPairModal] = useState<{
		plan: InterestPairEditPlan;
		onProceed?: () => void;
	} | null>(null);
	const queryClient = useQueryClient();

	const { deal, refetchCapValidationDetails } = useContext(DealContext);
	const { selectedEntityId, refetchPersons, personsSelectOptions, mipSelectOptions } = useContext(
		CapManageControllerContext
	);

	const updateTransactionsRequest = useMutation({
		mutationKey: [formatUnicorn(API_PATHS.CAP_TRANSACTION_UPDATE_POST, {})],
		mutationFn: updateTransaction,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.transaction_update_failed')
			),
	});

	const editTransactionForm = useForm<UpdateTransactionDto>({
		mode: 'all',
		resolver: zodResolver(UpdateTransactionShapeStrict(transaction)),
		defaultValues: transaction as unknown as UpdateTransactionDto,
		shouldFocusError: false,
	});

	const sellerId = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SELLER);
	const buyerId = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.BUYER);
	const transactionDate = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.DATE);
	const securityId = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SECURITY_ID);
	const selectedSecurity = resolveTransactionFormSecurity({
		securityId,
		securities,
		transactionSecurity: transaction?.security,
		dealHasNomineeStructure: deal?.hasNomineeStructure,
	});

	const getMipsForPersonByDateRequest = useMutation({
		mutationFn: getMipsForPersonByDate,
	});

	useEffect(() => {
		if (!sellerId || !transactionDate || !securityId || !transaction) {
			return;
		}
		getMipsForPersonByDateRequest.mutate({
			sellerId: sellerId || '',
			transactionDate: formatDateToBEIgnoreTimezone(transactionDate),
			securityId,
			transactionId: transaction.id,
		});
	}, [sellerId, transactionDate, securityId, transaction]);

	useEffect(() => {
		if (!transaction) {
			return;
		}
		editTransactionForm.reset({
			destinationId: transaction.destinationId,
			fromMipId: transaction.fromMipId,
			id: transaction.id,
			units: Number(transaction.units),
			cost: Number(transaction.cost),
			securityId: transaction.securityId,
			sourceId: transaction.sourceId || '',
			soldForId: transaction.soldForId,
			boughtForId: transaction.boughtForId,
			toMipId: transaction.toMipId,
			// See INTERNAL_ONLY_TRANSACTION_TYPES in transactions.zod.ts.
			type:
				transaction.type === TransactionType.VESTING_EVENT
					? null
					: (transaction.type as UserSubmittableTransactionType),
			date: transaction.date ? parseBEDateIgnoreTimezone(transaction.date) : undefined,
			conversionTargetSecurityId: transaction.conversionTargetSecurityId || undefined,
			participationThresholdExpressedAs: transaction?.participationThresholdExpressedAs,
			participationThreshold: transaction?.participationThreshold,
			securityPersonId: transaction?.security?.issuerId,
			// CAP-1975: seed the nominee interest so the TRANSFER interest selector reflects the
			// transaction's current legal/beneficial title (and preserves it if unchanged).
			title: transaction?.title,
		});
	}, [transaction]);

	const transactionType = editTransactionForm.watch(
		EDIT_TRANSACTION_FORM_FIELD.TYPE
	) as TransactionType;

	// CAP-1062: the feature flag keeps every nominee surface dark. The loaded transaction's title
	// is still preserved on submit — only the interest UI is hidden.
	const nomineeStructuresEnabled = useDealstackFeatureFlag(
		FEATURE_FLAG.CAP_NOMINEE_STRUCTURES
	).value;

	// CAP-1976: a TRANSFER, REDEMPTION or CANCELLATION on a nominee security exposes its interest as
	// an editable dropdown (Beneficial / Legal / Both), so the user can move or retire only the legal
	// title, only the beneficial interest, or both. Single-title rows are editable too: a register
	// that ends up inconsistent is accepted rather than refused at the edit. Note the grid does NOT
	// flag that inconsistency on a title-split deal — see isNomineeInterestEditable for why.
	const showInterestSelect =
		nomineeStructuresEnabled && isNomineeInterestEditable(transactionType, selectedSecurity);

	// CAP-2033: a leg of a resolved inseparable redemption has a FIXED interest — its counterpart legs
	// were derived from the serials the named rail held, so retitling it alone unpicks that
	// correspondence. CAP-1976 makes the interest editable again by editing the OPERATION, so this is
	// now the fallback for when the operation could not be loaded: shown disabled with the reason
	// rather than hidden, because an absent control is what made the old behaviour so hard to read.
	const interestFixedByRedemptionGroup = !!transaction?.redemptionGroupId;
	const interestFixedReason = interestFixedByRedemptionGroup
		? tCapManage('transactions.interest_fixed.redemption_group')
		: undefined;
	const interestOptions = useMemo(() => getTransferInterestOptions(tCapManage), [tCapManage]);
	// An operation is resolved against one rail, so Both is not one of its answers. Offering it let
	// the user pick a value the payload could not carry.
	const pairInterestOptions = useMemo(
		() =>
			interestOptionsForPairedLeg(
				interestOptions,
				transaction?.title,
				transaction?.interestPairId
			),
		[interestOptions, transaction?.title, transaction?.interestPairId]
	);

	const operationInterestOptions = useMemo(
		() =>
			interestOptions.filter(
				(option) => option.value !== TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE
			),
		[interestOptions]
	);

	/**
	 * CAP-1976: a leg of an inseparable redemption is not a row the user wrote — it is one of the
	 * resolver's answers to an operation they entered. So when the drawer opens on one, it opens on
	 * the OPERATION: the intent is read back, the form binds to that, and saving PATCHes new intent
	 * for the server to re-resolve. That is what makes seller, units and date editable at all — a
	 * row-at-a-time edit can only refuse them, because the answers to those questions are the other
	 * legs it cannot see.
	 */
	const redemptionGroupId = transaction?.redemptionGroupId;
	const redemptionGroupRequest = useQuery({
		queryKey: [
			API_PATHS.CAP_TRANSACTION_INSEPARABLE_REDEMPTION_REDEMPTIONGROUPID_GET,
			redemptionGroupId,
		],
		queryFn: () => getInseparableRedemptionGroup(redemptionGroupId ?? ''),
		enabled: !!redemptionGroupId,
	});
	const redemptionGroup = redemptionGroupRequest.data;
	const isRedemptionOperation = !!redemptionGroup;

	const previewRedemptionRequest = useMutation({
		mutationFn: previewInseparableRedemption,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.inseparable_redemption_preview_failed')
			),
	});
	const updateRedemptionRequest = useMutation({
		mutationFn: updateInseparableRedemption,
		onError: (error: unknown) =>
			transactionMutationError(
				error,
				tDefault,
				tCapManage('toasts.errors.inseparable_redemption_failed')
			),
	});
	const [pendingRedemption, setPendingRedemption] = useState<{
		payload: UpdateInseparableRedemptionDto;
		disclosure: InseparableRedemptionDisclosure;
		onDone: () => void;
	} | null>(null);

	// The form shows the OPERATION's totals, not this leg's share: a per-leg share is how the
	// resolver partitioned the take, so editing it would be editing the answer, not the question.
	useEffect(() => {
		if (!redemptionGroup) {
			return;
		}
		editTransactionForm.reset({
			...editTransactionForm.getValues(),
			sourceId: redemptionGroup.holderId,
			units: redemptionGroup.units,
			cost: redemptionGroup.cost,
			securityId: redemptionGroup.securityId,
			fromMipId: redemptionGroup.fromMipId,
			soldForId: redemptionGroup.soldForId,
			date: parseBEDateIgnoreTimezone(redemptionGroup.date),
			type: redemptionGroup.type as UserSubmittableTransactionType,
			title:
				redemptionGroup.interest === CapTableRail.BENEFICIAL
					? TransactionTitle.BENEFICIAL_INTEREST
					: TransactionTitle.LEGAL_TITLE,
		});
	}, [redemptionGroup]);

	/**
	 * Set when the row opened is the counterpart rather than the rail the operation is recorded
	 * against. The drawer binds to the OPERATION, so its Interest and Seller are the operation's,
	 * not this row's -- without saying so, the screen reads as the legal row claiming a beneficial
	 * seller, and an edit to Seller looks like it moved the wrong rail.
	 */
	const operationRailNote = useMemo(() => {
		if (!redemptionGroup || !transaction?.title) {
			return undefined;
		}
		const operationTitle =
			redemptionGroup.interest === CapTableRail.BENEFICIAL
				? TransactionTitle.BENEFICIAL_INTEREST
				: TransactionTitle.LEGAL_TITLE;
		if (transaction.title === operationTitle) {
			return undefined;
		}
		return tCapManage('transactions.inseparable_redemption.viewing_counterpart', {
			rowRail: getTransactionInterestText(transaction.title, tCapManage),
			operationRail: getTransactionInterestText(operationTitle, tCapManage),
		});
	}, [redemptionGroup, transaction?.title, tCapManage]);

	const holderNameById = (id: string): string =>
		personsSelectOptions?.find((option) => option.value === id)?.label ??
		tCapManage('transactions.inseparable_redemption.unknown_holder');

	/**
	 * CAP-2039: what the EDITED intent would retire, resolved live, alongside the panel below showing
	 * what the group retires today. The two answer different questions and the drawer needs both: the
	 * operation editor's whole purpose is to change units, seller or partition, and every one of those
	 * moves the counterpart — so committing without seeing the new answer is the same guidance gap the
	 * create drawer had, on the surface where the values are more likely to be wrong.
	 *
	 * `replacesRedemptionGroupId` is what makes an unchanged re-resolve possible: without it the
	 * group's own retirement counts against itself and the panel would show a refusal on a form the
	 * user has not touched.
	 */
	const editedInterest = railFromTitle(
		editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.TITLE)
	);
	const redemptionCounterpartPreview = useRedemptionCounterpartPreview(
		{
			securityId,
			holderId: sellerId,
			interest: editedInterest,
			units: Number(editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.NUMBER) ?? 0),
			cost: Number(editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.PRICE) ?? 0),
			date: transactionDate,
			type: transactionType,
			fromMipId: editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP),
			soldForId: editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SOLD_FOR),
			replacesRedemptionGroupId: redemptionGroup?.redemptionGroupId,
		},
		isRedemptionOperation && !!editedInterest
	);

	/**
	 * What the group retires ALONGSIDE the leg being viewed, for the panel shown while the drawer is
	 * merely open. No serial ranges: they are not stored, and re-deriving them costs a whole fold —
	 * too much for opening a drawer. The serials appear in the confirmation on save, where a fold is
	 * paid for anyway.
	 *
	 * The group reads back from the CHOOSING side whichever leg was opened, so "alongside" has to be
	 * worked out here: open the counterpart leg and its own holder is in `counterparts`, which read
	 * as "Nominee Custodian Ltd retires alongside Nominee Custodian Ltd". The chooser side is one
	 * holder by construction — every chooser leg shares the operation's holderId — so naming it
	 * flattens nothing.
	 */
	const redemptionCounterparts = useMemo(() => {
		// A SPLIT retirement is a pair, not a resolved group (see
		// cap2034Cap2035InterestAndTypeEdits), so it has no group to read counterparts from -- and
		// the panel used to go blank, leaving the confirm dialog's promise of legs that "delete
		// together" with no way to see or reach the other one. The sibling summary the API already
		// returns is enough: a split retitles ONE row into two over the SAME parties, so the
		// counterpart is this row's own seller on the other rail.
		if (!redemptionGroup) {
			const sibling = transaction?.interestPairSibling;
			const retiring =
				transaction?.type === TransactionType.REDEMPTION ||
				transaction?.type === TransactionType.CANCELLATION;
			if (!sibling?.title || !retiring || !transaction?.sourceId) {
				return [];
			}
			return [
				{
					holderName: holderNameById(transaction.sourceId),
					units: sibling.units ?? 0,
					title: sibling.title,
				},
			];
		}
		const chooserTitle =
			redemptionGroup.interest === CapTableRail.BENEFICIAL
				? TransactionTitle.BENEFICIAL_INTEREST
				: TransactionTitle.LEGAL_TITLE;
		// A resolved group leg always carries one single title, never Both, so a direct compare is safe.
		const viewingChooser = transaction?.title === chooserTitle;

		return viewingChooser
			? (redemptionGroup.counterparts ?? []).map((counterpart) => ({
					holderName: holderNameById(counterpart.holderId),
					units: counterpart.units,
					title: counterpart.title,
				}))
			: [
					{
						holderName: holderNameById(redemptionGroup.holderId),
						units: redemptionGroup.units,
						title: chooserTitle,
					},
				];
	}, [redemptionGroup, personsSelectOptions, transaction?.title]);

	const submitRedemptionOperation = async (
		formValues: UpdateTransactionDto,
		onDone: () => void
	) => {
		if (!redemptionGroup) {
			return;
		}
		// A rail, not a title: CapTableRail has two values and the form field has three. "Both" used
		// to fall into the else, become BENEFICIAL, match what was stored, and PATCH a no-op that
		// returned 200 with "Transaction updated successfully" while nothing changed. An operation
		// is resolved against ONE rail, so Both is not a value it can take -- it is dropped from the
		// options in operation mode, and refused here rather than coerced if it arrives anyway.
		const interest = railFromTitle(formValues.title);
		if (!interest) {
			showToastV2({
				state: 'error',
				message: tCapManage('toasts.errors.redemption_operation_interest_both'),
			});
			return;
		}
		const payload: UpdateInseparableRedemptionDto = {
			redemptionGroupId: redemptionGroup.redemptionGroupId,
			date: formatDateToBEIgnoreTimezone(formValues.date),
			securityId: formValues.securityId,
			holderId: formValues.sourceId || '',
			interest,
			units: Number(formValues.units),
			cost: Number(formValues.cost),
			type:
				formValues.type === TransactionType.CANCELLATION
					? TransactionType.CANCELLATION
					: TransactionType.REDEMPTION,
			// Omitted rather than null: the resolver reads "did the caller name this axis?" from key
			// presence, and leaving both unset is what lets it span every position the holder has.
			...(formValues.fromMipId ? { fromMipId: formValues.fromMipId } : {}),
			...(formValues.soldForId ? { soldForId: formValues.soldForId } : {}),
		};

		try {
			// The preview must measure the register as it would be WITHOUT this group, or the units
			// it already retired count against itself and the re-resolve refuses a take the save
			// would have accepted. The save discounts them by soft-deleting the legs first, so the
			// group id is named only here.
			const preview = await previewRedemptionRequest.mutateAsync({
				...payload,
				replacesRedemptionGroupId: redemptionGroup.redemptionGroupId,
			});
			const legs = preview?.legs ?? [];
			if (!legs.length) {
				showToastV2({
					state: 'error',
					message: tCapManage('toasts.errors.inseparable_redemption_no_legs'),
				});
				return;
			}
			setPendingRedemption({
				// CAP-2039: the PATCH carries the legs the disclosure just named, so it is refused
				// (120254) if the re-resolve at write time answers differently. This edit path is
				// the more exposed of the two: the drawer can sit open on a group for as long as the
				// user likes while other people write to the same register.
				payload: { ...payload, disclosedLegs: legs.map(toLegFingerprint) },
				onDone,
				disclosure: buildInseparableRedemptionDisclosure(
					legs,
					interest,
					selectedSecurity?.name ?? '',
					holderNameById,
					tCapManage
				),
			});
		} catch {
			// The mutation's own onError has already told the user; this only stops the flow.
		}
	};

	const confirmRedemptionOperation = async () => {
		if (!pendingRedemption) {
			return;
		}
		try {
			await updateRedemptionRequest.mutateAsync(pendingRedemption.payload);
			const { onDone } = pendingRedemption;
			setPendingRedemption(null);
			await redemptionGroupRequest.refetch();
			onDone();
		} catch {
			// Reported by the mutation's onError. Deliberately leaves pendingRedemption set so the
			// confirmation stays open on the values the user was trying to commit.
		}
	};

	const issuerId =
		transaction?.security?.issuerId ||
		editTransactionForm.getValues(CREATE_TRANSACTION_FORM_FIELD.ENTITY_ID) ||
		selectedEntityId;

	const buildUpdatePayload = (formValues: UpdateTransactionDto) => ({
		id: transaction?.id as string,
		type: (formValues.type as TransactionType) || null,
		date: formatDateToBEIgnoreTimezone(formValues.date),
		securityId: formValues.securityId,
		units: Number(formValues.units),
		cost: Number(formValues.cost),
		sourceId: formValues.sourceId || '',
		destinationId: formValues.destinationId || '',
		soldForId: formValues.soldForId,
		boughtForId: formValues.boughtForId,
		fromMipId: formValues.fromMipId || null,
		toMipId: formValues.toMipId || null,
		conversionTargetSecurityId: formValues.conversionTargetSecurityId || undefined,
		// CAP-1975: persist the nominee interest. Undefined for non-nominee transactions and
		// preserved from the loaded transaction unless the TRANSFER interest selector changed it.
		title: formValues.title ?? undefined,
		participationThresholdExpressedAs:
			(formValues.participationThresholdExpressedAs as TransactionThresholdExpressedAs) ||
			null,
		participationThreshold: formValues.participationThreshold,
	});

	// CAP-1976: a successful edit of a paired row may also change the linked leg — its details
	// page and audit trail must not serve stale caches.
	const invalidateInterestPairSiblingCaches = () => {
		const siblingId = transaction?.interestPairSibling?.id;
		if (!siblingId) {
			return;
		}
		void queryClient.invalidateQueries({
			queryKey: [API_PATHS.CAP_TRANSACTION_TRANSACTION_DETAILS_POST, siblingId],
		});
		void queryClient.invalidateQueries({
			queryKey: [formatUnicorn(API_PATHS.AUDIT_ENTITY_ENTITYID_GET, { entityId: siblingId })],
		});
	};

	// CAP-1976: run the pair rules over the dirty fields before submitting. Blocked changes stop
	// here; sync/warn/split outcomes open the dialog and submit on confirmation (sync outcomes
	// resubmit with confirmPairSync — the server recomputes the synced values itself and answers
	// 120242-120245 as the backstop for anything the dialog missed).
	const submitWithInterestPairGuard = (
		formValues: UpdateTransactionDto,
		onSuccess: (updatedTransactionId?: string) => void
	) => {
		const payload = buildUpdatePayload(formValues);
		const send = (confirmPairSync?: boolean) =>
			// Only onSuccess here: the failure path lives on the mutation itself, because a
			// per-call onError is additive to ApiLayer's default (two toasts) and is dropped
			// when this component unmounts mid-flight (no toast at all).
			updateTransactionsRequest.mutate(
				confirmPairSync ? { ...payload, confirmPairSync } : payload,
				{ onSuccess: (updated) => onSuccess(updated?.id) }
			);

		// CAP-1976: the operation editor answers a redemption's OWN questions — who it takes from,
		// how much, when. Retyping it into something that is not a retirement is a different
		// transaction, not a re-resolution: coercing it back to REDEMPTION here previewed an issue
		// as a redemption and asked the resolver what the ISSUER holds on the rail (120246). Let it
		// fall through so the pair/group rules refuse it in the words that fit.
		const staysRetirement =
			formValues.type === TransactionType.REDEMPTION ||
			formValues.type === TransactionType.CANCELLATION;
		if (isRedemptionOperation && staysRetirement) {
			void submitRedemptionOperation(formValues, () => onSuccess(transaction?.id));
			return;
		}

		// In operation mode the form was loaded with the OPERATION's rail, which is the chooser
		// leg's. Opening a counterpart leg therefore makes form.title differ from this row's title
		// before the user touches anything, and comparing against the row read that as an interest
		// change: changing the TYPE on a legal leg raised "Interest is fixed by the linked leg" and
		// offered Delete pair, for a field nobody had edited. Compare against the operation's rail
		// so only a real change registers.
		const interestBaseline =
			transaction && redemptionGroup
				? {
						...transaction,
						title:
							redemptionGroup.interest === CapTableRail.BENEFICIAL
								? TransactionTitle.BENEFICIAL_INTEREST
								: TransactionTitle.LEGAL_TITLE,
					}
				: transaction;
		const plan = resolveInterestPairEditPlan(interestBaseline, formValues, tCapManage);
		if (!plan) {
			send();
			return;
		}
		if (plan.kind === 'blocked') {
			setInterestPairModal({ plan });
			return;
		}
		setInterestPairModal({
			plan,
			// The merge asks for the same confirmation flag as a sync: both are edits the server
			// refuses to make silently because they reach the linked leg.
			onProceed: () => send(plan.kind === 'confirm' || plan.kind === 'mergePreview'),
		});
	};

	const onSubmit = (formValues: UpdateTransactionDto) => {
		submitWithInterestPairGuard(formValues, () => {
			if (refreshTransactionsList) {
				refreshTransactionsList();
			}
			void refetchCapValidationDetails();
			showToastV2({
				title: tCapManage('toasts.success.transaction_updated_successfully'),
			});
			void refetchPersons();
			invalidateInterestPairSiblingCaches();
			if (onClose) {
				onClose();
			}
		});
	};

	const onSubmitDetails = (formValues: UpdateTransactionDto) => {
		submitWithInterestPairGuard(formValues, (updatedTransactionId) => {
			showToastV2({
				title: tCapManage('toasts.success.transaction_updated_successfully'),
			});

			if (updatedTransactionId && updatedTransactionId !== transaction?.id) {
				// The row was replaced by an interest split (Both -> single on a
				// REDEMPTION/CANCELLATION): land on the returned twin — the old id is soft-deleted.
				if (onTransactionReplaced) {
					onTransactionReplaced(updatedTransactionId);
				}
			} else if (refetchTransactionDetails) {
				refetchTransactionDetails(transaction?.id || '');
			}

			void refetchCapValidationDetails();

			void queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(API_PATHS.AUDIT_ENTITY_ENTITYID_GET, {
						entityId: transaction?.id || '',
					}),
				],
			});
			invalidateInterestPairSiblingCaches();

			setEditMode(false);
		});
	};

	const sellerMipSelectOptions = useMemo(
		() => getMipOptionsForSelect(getMipsForPersonByDateRequest.data),
		[getMipsForPersonByDateRequest.data]
	);

	const { hideSeller, hideBuyer, hideBuyerMip, hideSellerMip, hideUnits, hideCost } =
		useTransactionFieldsVisibility(transactionType, selectedSecurity?.issuedIn);

	const { hideBoughtFor, hideSoldFor, boughtForOptions, soldForOptions } =
		useTransactionPivRelations({
			buyerId,
			sellerId,
			personsSelectOptions,
			setValue: editTransactionForm.setValue,
			getValues: editTransactionForm.getValues,
			boughtForField: EDIT_TRANSACTION_FORM_FIELD.BOUGHT_FOR,
			soldForField: EDIT_TRANSACTION_FORM_FIELD.SOLD_FOR,
		});

	const { unitsDecimalPoints } = useTransactionPartyDefaults({
		transactionType,
		selectedSecurity,
		issuerId,
		getValues: editTransactionForm.getValues,
		setValue: editTransactionForm.setValue,
		clearErrors: editTransactionForm.clearErrors,
		fields: {
			buyer: CREATE_TRANSACTION_FORM_FIELD.BUYER,
			seller: CREATE_TRANSACTION_FORM_FIELD.SELLER,
			price: CREATE_TRANSACTION_FORM_FIELD.PRICE,
			units: CREATE_TRANSACTION_FORM_FIELD.NUMBER,
		},
	});

	return {
		updateTransactionsRequest,
		sellerMipSelectOptions,
		onSubmit,
		refetchPersons,
		editTransactionForm,
		personsSelectOptions,
		editMode,
		setEditMode,
		onSubmitDetails,
		mipSelectOptions,
		hideBoughtFor,
		hideSoldFor,
		boughtForOptions,
		soldForOptions,
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		costLabel: getTransactionCostLabel(transactionType),
		hideCost,
		hideUnits,
		unitsDecimalPoints,
		showInterestSelect,
		interestFixedByRedemptionGroup,
		operationInterestOptions,
		pairInterestOptions,
		operationRailNote,
		interestFixedReason,
		interestOptions,
		// CAP-1976: operation mode. The drawer hides buyer (a redemption's buyer is the issuer by
		// definition), shows what the redemption retires alongside, and confirms through the same
		// disclosure the create flow uses.
		isRedemptionOperation,
		redemptionCounterparts,
		redemptionCounterpartPreview,
		// The rail the panel labels its counterpart against. Beneficial is the fallback purely so the
		// panel has a value while `title` is momentarily unset; it renders nothing in that state.
		redemptionPreviewInterest: editedInterest ?? CapTableRail.BENEFICIAL,
		redemptionSegmentCount: redemptionGroup?.segmentCount ?? 0,
		pendingRedemption,
		confirmRedemptionOperation,
		cancelRedemptionOperation: () => setPendingRedemption(null),
		isRedemptionOperationPending:
			previewRedemptionRequest.isPending || updateRedemptionRequest.isPending,
		nomineeStructuresEnabled,
		interestPairModal,
		closeInterestPairModal: () => setInterestPairModal(null),
		selectedSecurity,
	};
};
